import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Auth } from 'firebase/auth';
import { catchError, first, from, map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../auth.service';
import { ConsentStore } from '../consent-store';

/**
 * Blocca chi è autenticato ma non ha i consensi obbligatori nella versione vigente:
 * utenti creati da un amministratore, utenti arrivati dall'app mobile, e chiunque
 * debba riaccettare dopo la pubblicazione di una nuova versione dei documenti.
 *
 * Chi non è autenticato passa sempre: la navigazione pubblica non cambia.
 */
export const ConsentsGuard: CanActivateFn = (
  route,
  state,
): Observable<boolean | UrlTree> => {
  const auth: Auth = inject(AuthService).auth();
  const router: Router = inject(Router);
  const consentStore = inject(ConsentStore);

  return from(auth.authStateReady()).pipe(
    first(),
    switchMap(() => {
      const user = auth.currentUser;

      // Navigazione pubblica: nessun consenso da verificare.
      // L'email non verificata è competenza di EmailVerificationGuard.
      if (!user || !user.emailVerified) return of(true);

      return consentStore.load().pipe(
        map((status) => {
          if (status.missingRequired.length === 0) return true;

          const redirectURL =
            state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;

          return router.parseUrl(`consents-required?${redirectURL}`);
        }),
        // Se lo stato non è raggiungibile non si blocca l'utente fuori dal sito:
        // meglio lasciarlo navigare che presentargli una pagina bianca.
        catchError(() => of(true)),
      );
    }),
  );
};
